import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar, MessageSquare, Car, Award, Edit2, Upload, Lock, Send } from 'lucide-react';
import { toast } from 'sonner';
import { formatError } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import PushNotificationToggle from '../components/PushNotificationToggle';
import SEO from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
}

const BADGE_COLORS = {
  forum: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  event: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  special: 'bg-violet-50 text-violet-700 border-violet-200',
};

export default function UserProfilePage() {
  const { userId } = useParams();
  const { user: me, updateUser } = useAuth();
  const navigate = useNavigate();
  const [dmLoading, setDmLoading] = useState(false);

  const startDM = async () => {
    if (!me) { navigate('/giris'); return; }
    if (!profile?._id || profile._id === me._id) return;
    setDmLoading(true);
    try {
      const r = await axios.post(
        `${API}/messages/conversations`,
        { participant_ids: [profile._id] },
        { withCredentials: true }
      );
      navigate(`/mesajlar/${r.data.conversation._id}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setDmLoading(false);
    }
  };
  const [profile, setProfile] = useState(null);
  const [topics, setTopics] = useState([]);
  const [badges, setBadges] = useState([]);
  const [garage, setGarage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [editForm, setEditForm] = useState({ display_name: '', bio: '' });
  const [saving, setSaving] = useState(false);
  const isOwnProfile = me?._id === userId || me?.username === userId;

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/users/${userId}/profile`);
      setProfile(r.data.user);
      setTopics(r.data.topics || []);
      setBadges(r.data.badges || []);
      setGarage(r.data.garage || []);
      setEditForm({ display_name: r.data.user.display_name || '', bio: r.data.user.bio || '' });
    } catch {
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProfile(); }, [userId]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await axios.put(`${API}/auth/me`, editForm, { withCredentials: true });
      setProfile(prev => ({ ...prev, ...editForm }));
      updateUser(r.data.user);
      setEditOpen(false);
      toast.success('Profil güncellendi!');
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  const handleAvatarUpload = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const r = await axios.post(`${API}/auth/upload-avatar`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfile(prev => ({ ...prev, avatar_url: r.data.path }));
      updateUser({ ...me, avatar_url: r.data.path });
      toast.success('Profil fotoğrafı güncellendi!');
    } catch (err) { toast.error(formatError(err)); }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="w-10 h-10 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <p className="text-slate-500">Kullanıcı bulunamadı</p>
      </div>
    );
  }

  const avatarUrl = profile.avatar_url ? (profile.avatar_url.startsWith('http') ? profile.avatar_url : `${API}/files/${profile.avatar_url}`) : '';
  const initials = (profile.display_name || profile.username || 'U').slice(0, 2).toUpperCase();

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO
        title={profile ? `${profile.display_name || profile.username} · Profil` : 'Profil'}
        description={profile?.bio || 'MiniOto topluluk üyesi profili.'}
        noindex
      />
      {/* Profile header */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
        <div className="flex items-start gap-5">
          <div className="relative">
            <Avatar className="w-20 h-20">
              <AvatarImage src={avatarUrl} />
              <AvatarFallback className="bg-emerald-100 text-emerald-700 text-2xl font-extrabold">{initials}</AvatarFallback>
            </Avatar>
            {isOwnProfile && (
              <label className="absolute -bottom-1 -right-1 w-7 h-7 bg-emerald-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-emerald-700 transition-colors shadow-sm" data-testid="avatar-upload-label">
                <Upload className="w-3.5 h-3.5 text-white" />
                <input type="file" accept="image/*" className="hidden" onChange={e => handleAvatarUpload(e.target.files[0])} />
              </label>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-extrabold text-slate-800 break-words">{profile.display_name || profile.username}</h1>
                <p className="text-slate-400 text-sm">@{profile.username}</p>
                {profile.role !== 'user' && (
                  <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full font-semibold ${profile.role === 'admin' ? 'bg-red-50 text-red-700' : 'bg-orange-50 text-orange-700'}`}>
                    {profile.role === 'admin' ? 'Yönetici' : 'Moderatör'}
                  </span>
                )}
              </div>
              {isOwnProfile && (
                <div className="flex items-center gap-2 flex-wrap sm:justify-end" data-testid="profile-actions">
                  <Button variant="outline" size="sm" onClick={() => setPasswordOpen(true)} data-testid="change-password-btn">
                    <Lock className="w-3.5 h-3.5 mr-1.5" /> Şifre
                  </Button>
                  <Dialog open={editOpen} onOpenChange={setEditOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="edit-profile-btn">
                        <Edit2 className="w-3.5 h-3.5 mr-1.5" /> Düzenle
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader><DialogTitle>Profili Düzenle</DialogTitle></DialogHeader>
                      <form onSubmit={handleSaveProfile} className="space-y-4 mt-2">
                        <div>
                          <Label>Ad Soyad</Label>
                          <Input value={editForm.display_name} onChange={e => setEditForm(p => ({...p, display_name: e.target.value}))} className="mt-1" data-testid="profile-name-input" />
                        </div>
                        <div>
                          <Label>Hakkımda</Label>
                          <Textarea value={editForm.bio} onChange={e => setEditForm(p => ({...p, bio: e.target.value}))} rows={3} className="mt-1 resize-none" placeholder="Kendinizden bahsedin..." data-testid="profile-bio-input" />
                        </div>
                        <Button type="submit" disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-profile-btn">
                          {saving ? 'Kaydediliyor...' : 'Kaydet'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
              {!isOwnProfile && (
                <div className="flex items-center gap-2 flex-wrap sm:justify-end" data-testid="profile-actions-other">
                  <Button
                    onClick={startDM}
                    disabled={dmLoading}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="profile-dm-btn"
                  >
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                    {dmLoading ? 'Açılıyor...' : 'Mesaj Gönder'}
                  </Button>
                </div>
              )}
            </div>
            {profile.bio && <p className="text-slate-600 mt-2">{profile.bio}</p>}
            <div className="flex items-center gap-1 text-xs text-slate-400 mt-3">
              <Calendar className="w-3.5 h-3.5" />
              <span>{formatDate(profile.created_at)} tarihinde katıldı</span>
            </div>
          </div>
        </div>
        {isOwnProfile && <PushNotificationToggle />}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mt-6 pt-5 border-t border-slate-100">
          <div className="text-center">
            <div className="text-xl font-extrabold text-slate-800">{profile.post_count || 0}</div>
            <div className="text-xs text-slate-400">Konu</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-extrabold text-slate-800">{profile.reply_count || 0}</div>
            <div className="text-xs text-slate-400">Yorum</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-extrabold text-slate-800">{badges.length}</div>
            <div className="text-xs text-slate-400">Rozet</div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="topics">
        <TabsList className="mb-5">
          <TabsTrigger value="topics" data-testid="profile-topics-tab">
            <MessageSquare className="w-4 h-4 mr-1.5" /> Konular ({topics.length})
          </TabsTrigger>
          <TabsTrigger value="badges" data-testid="profile-badges-tab">
            <Award className="w-4 h-4 mr-1.5" /> Rozetler ({badges.length})
          </TabsTrigger>
          <TabsTrigger value="garage" data-testid="profile-garage-tab">
            <Car className="w-4 h-4 mr-1.5" /> Garaj ({garage.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="topics">
          {topics.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-2xl border border-slate-100">
              <MessageSquare className="w-10 h-10 text-slate-200 mx-auto mb-2" />
              <p className="text-slate-500">Henüz konu yok</p>
            </div>
          ) : (
            <div className="space-y-2">
              {topics.map(t => (
                <Link key={t._id} to={`/forum/konu/${t.slug || t._id}`} data-testid={`profile-topic-${t._id}`}
                  className="block bg-white rounded-xl border border-slate-200 hover:border-emerald-200 p-4 transition-colors">
                  <div className="font-semibold text-slate-800">{t.title}</div>
                  <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                    <span>{t.category_name}</span>
                    <span>{formatDate(t.created_at)}</span>
                    <span>{t.reply_count || 0} yanıt</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="badges">
          {badges.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-2xl border border-slate-100">
              <Award className="w-10 h-10 text-slate-200 mx-auto mb-2" />
              <p className="text-slate-500">Henüz rozet yok</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {badges.map(b => (
                <div key={b.key} data-testid={`profile-badge-${b.key}`}
                  className={`rounded-xl border p-3 text-center ${BADGE_COLORS[b.category] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                  <div className="text-2xl mb-1">
                    {b.key === 'ilk_konu' ? '' : b.key === 'ilk_yorum' ? '' : b.key === 'on_yorum' ? '⭐' : b.key === 'aktif_uye' ? '' : b.key === 'etkinlik_katilimcisi' ? '' : b.key.includes('uzman') ? '' : ''}
                  </div>
                  <div className="font-bold text-sm">{b.name}</div>
                  <div className="text-xs opacity-70 mt-0.5">{formatDate(b.earned_at)}</div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="garage">
          {garage.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-2xl border border-slate-100">
              <Car className="w-10 h-10 text-slate-200 mx-auto mb-2" />
              <p className="text-slate-500">Garaj boş</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {garage.map(v => (
                <div key={v._id} data-testid={`profile-garage-${v._id}`} className="bg-white rounded-xl border border-slate-200 p-4">
                  {v.photo_path && (
                    <img
                      src={v.photo_path.startsWith('http') ? v.photo_path : `${API}/files/${v.photo_path}`}
                      alt={v.model}
                      className="w-full h-32 object-cover rounded-lg mb-3"
                    />
                  )}
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${v.category === 'L6e' ? 'bg-emerald-100 text-emerald-700' : 'bg-cyan-100 text-cyan-700'}`}>{v.category}</span>
                  </div>
                  {v.custom_name && <div className="text-xs text-emerald-600 font-medium">"{v.custom_name}"</div>}
                  <div className="font-bold text-slate-800">{v.brand} {v.model}</div>
                  <div className="text-xs text-slate-400">{v.year}</div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} />
    </div>
  );
}
